import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calendar, Loader2 } from "lucide-react";

export default function CreateEventModal({ isOpen, onClose, onSuccess }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim() || !eventDate) {
      toast.error("Please enter title and date");
      return;
    }

    setLoading(true);
    try {
      await onSuccess({ title, description, location, event_date: eventDate, event_time: eventTime });
      setTitle("");
      setDescription("");
      setLocation("");
      setEventDate("");
      setEventTime("");
      onClose();
    } catch {
      // Error handled in parent
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Create Event
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Title *</label>
            <Input 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              className="border-[#E5E0D5]"
              data-testid="event-title-input"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Description</label>
            <Textarea 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the event..."
              className="border-[#E5E0D5] min-h-[80px]"
              data-testid="event-description-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Date *</label>
              <Input 
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="border-[#E5E0D5]"
                data-testid="event-date-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Time</label>
              <Input 
                type="time"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
                className="border-[#E5E0D5]"
                data-testid="event-time-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-[#2D2A26] font-['Nunito']">Location</label>
            <Input 
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Event location"
              className="border-[#E5E0D5]"
              data-testid="event-location-input"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white" data-testid="create-event-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
            Create Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
